import type { MantineColorsTuple } from '@mantine/core';

/**
 * Apparence reprise du logiciel « Gestion Bulletins » : mêmes couleurs principales
 * (teinte claire pour le thème clair, teinte lumineuse pour le thème sombre),
 * mêmes polices et mêmes tailles de texte.
 */
export type AccentKey = 'vert' | 'bleu' | 'marine' | 'violet' | 'orange' | 'rouge' | 'ardoise';
export type ThemeKey = 'clair' | 'sombre';
export type SizeKey = '13' | '15' | '16.5' | '18';
export type HeadingFontKey = 'texte' | 'Bricolage Grotesque';
export type IntensityKey = 'normal' | 'fonce' | 'tres_fonce';
export type WeightKey = 'normale' | 'moyenne' | 'grasse';
export type TextColorKey =
  'defaut' | 'noir' | 'bleu_nuit' | 'vert_fonce' | 'brun' | 'bordeaux' | 'violet' | 'ardoise';
export type PageBackgroundKey = 'uni' | 'degrade' | 'motif' | 'papier';
export type PrintBackgroundKey = 'aucun' | 'filigrane' | 'bandeau';

export interface Appearance {
  theme: ThemeKey;
  accent: AccentKey;
  font: string;
  size: SizeKey;
  heading_font: HeadingFontKey;
  text_intensity: IntensityKey;
  text_weight: WeightKey;
  text_color: TextColorKey;
  page_background: PageBackgroundKey;
  print_background: PrintBackgroundKey;
}

export const ACCENTS: Record<AccentKey, { label: string; light: string; dark: string }> = {
  vert: { label: 'Vert (défaut)', light: '#0e7765', dark: '#4cc4ab' },
  bleu: { label: 'Bleu', light: '#1d4ed8', dark: '#60a5fa' },
  marine: { label: 'Bleu marine', light: '#1e3a8a', dark: '#93c5fd' },
  violet: { label: 'Violet', light: '#6d28d9', dark: '#a78bfa' },
  orange: { label: 'Orange', light: '#c2410c', dark: '#fb923c' },
  rouge: { label: 'Bordeaux', light: '#9f1239', dark: '#fb7185' },
  ardoise: { label: 'Ardoise', light: '#334155', dark: '#94a3b8' },
};

/** Polices intégrées (sans installation) : les trois premières sont humanistes. */
const BUNDLED_FONTS: Record<string, string> = {
  Nunito: '"Nunito Variable", "Nunito"',
  'Source Sans 3': '"Source Sans 3 Variable", "Source Sans 3"',
  'Open Sans': '"Open Sans Variable", "Open Sans"',
  Figtree: '"Figtree Variable", "Figtree"',
};

export const UI_FONTS: [string, string][] = [
  ['Nunito', 'Nunito (humaniste, arrondie, défaut)'],
  ['Source Sans 3', 'Source Sans 3 (humaniste, sobre)'],
  ['Open Sans', 'Open Sans (humaniste, très lisible)'],
  ['Figtree', 'Figtree (moderne)'],
  ['Segoe UI', 'Segoe UI'],
  ['Calibri', 'Calibri'],
  ['Arial', 'Arial'],
  ['Verdana', 'Verdana'],
  ['Tahoma', 'Tahoma'],
  ['Trebuchet MS', 'Trebuchet MS'],
  ['Georgia', 'Georgia'],
  ['Cambria', 'Cambria'],
  ['Times New Roman', 'Times New Roman'],
];

export const SIZES: [SizeKey, string][] = [
  ['13', 'Petite'],
  ['15', 'Normale'],
  ['16.5', 'Grande'],
  ['18', 'Très grande'],
];

export const HEADING_FONTS: [HeadingFontKey, string][] = [
  ['texte', 'Identique au texte'],
  ['Bricolage Grotesque', 'Bricolage Grotesque (caractère)'],
];

export const INTENSITIES: [IntensityKey, string][] = [
  ['normal', 'Normale'],
  ['fonce', 'Foncée'],
  ['tres_fonce', 'Très foncée (noir)'],
];

export const WEIGHTS: [WeightKey, string][] = [
  ['normale', 'Normale'],
  ['moyenne', 'Moyenne'],
  ['grasse', 'Grasse'],
];

/**
 * Couleurs des écritures : teinte sur fond clair et sur fond sombre. « Par défaut »
 * garde le vert-noir du thème. L'intensité fonce ensuite la teinte choisie.
 */
export const TEXT_COLORS: Record<TextColorKey, { label: string; light: string; dark: string }> = {
  defaut: { label: 'Par défaut (vert-noir)', light: '#0a1f1b', dark: '#f1f7f5' },
  noir: { label: 'Noir', light: '#111111', dark: '#ffffff' },
  bleu_nuit: { label: 'Bleu nuit', light: '#16325c', dark: '#cfe0ff' },
  vert_fonce: { label: 'Vert foncé', light: '#0d4a3a', dark: '#c9f0e3' },
  brun: { label: 'Brun', light: '#4a2c17', dark: '#f3dcc8' },
  bordeaux: { label: 'Bordeaux', light: '#5c1426', dark: '#ffd3dd' },
  violet: { label: 'Violet foncé', light: '#3b1d6e', dark: '#e3d6ff' },
  ardoise: { label: 'Gris ardoise', light: '#1f2a37', dark: '#e2e8f0' },
};
export const TEXT_COLOR_OPTIONS = Object.entries(TEXT_COLORS).map(([value, c]) => [
  value,
  c.label,
]) as [TextColorKey, string][];

export const PAGE_BACKGROUNDS: [PageBackgroundKey, string][] = [
  ['uni', 'Uni'],
  ['degrade', 'Dégradé doux'],
  ['motif', 'Motif médical discret'],
  ['papier', 'Papier chaud'],
];

export const PRINT_BACKGROUNDS: [PrintBackgroundKey, string][] = [
  ['aucun', 'Blanc'],
  ['filigrane', 'Filigrane de la clinique'],
  ['bandeau', 'Bandeau de couleur'],
];

export const DEFAULT_APPEARANCE: Appearance = {
  theme: 'clair',
  accent: 'vert',
  font: 'Nunito',
  size: '15',
  heading_font: 'texte',
  text_intensity: 'fonce',
  text_weight: 'moyenne',
  text_color: 'defaut',
  page_background: 'degrade',
  print_background: 'filigrane',
};

export function uiFontStack(font: string): string {
  const main = BUNDLED_FONTS[font] ?? `"${font}"`;
  return `${main}, "Nunito Variable", "Segoe UI", system-ui, sans-serif`;
}

export function headingFontStack(a: Pick<Appearance, 'font' | 'heading_font'>): string {
  return a.heading_font === 'Bricolage Grotesque'
    ? '"Bricolage Grotesque Variable", "Bricolage Grotesque", system-ui, sans-serif'
    : uiFontStack(a.font);
}

/** Couleurs du texte (principal, secondaire) selon l'intensité et le thème. */
export const INK: Record<ThemeKey, Record<IntensityKey, [string, string]>> = {
  clair: {
    normal: ['#12302b', '#587069'],
    fonce: ['#0a1f1b', '#3e544e'],
    tres_fonce: ['#000000', '#24322e'],
  },
  sombre: {
    normal: ['#e2eeeb', '#9ab1ab'],
    fonce: ['#f1f7f5', '#b9ccc6'],
    tres_fonce: ['#ffffff', '#d8e4e0'],
  },
};

/** Texte principal et secondaire : couleur choisie, plus ou moins foncée selon l'intensité. */
export function inkColors(
  a: Pick<Appearance, 'theme' | 'text_intensity' | 'text_color'>,
): [string, string] {
  if (a.text_color === 'defaut') return INK[a.theme][a.text_intensity];
  const base = TEXT_COLORS[a.text_color];
  if (a.theme === 'sombre') {
    const toward = { normal: 0.15, fonce: 0, tres_fonce: -1 }[a.text_intensity];
    const ink = toward < 0 ? mix(base.dark, '#ffffff', 0.5) : mix(base.dark, '#0b1512', toward);
    return [ink, mix(ink, '#0b1512', 0.3)];
  }
  const darken = { normal: 0, fonce: 0.2, tres_fonce: 0.45 }[a.text_intensity];
  const ink = mix(base.light, '#000000', darken);
  return [ink, mix(ink, '#ffffff', 0.32)];
}

export const BODY_WEIGHT: Record<WeightKey, number> = { normale: 400, moyenne: 500, grasse: 600 };

/** Complète une apparence enregistrée avant l'ajout des réglages de lisibilité. */
export function withDefaults(input: Partial<Appearance>): Appearance {
  const value = input as Record<string, unknown>;
  const pick = <K extends keyof Appearance>(
    key: K,
    allowed: readonly (readonly [string, string])[],
  ) =>
    (allowed.some(([k]) => k === value[key])
      ? value[key]
      : DEFAULT_APPEARANCE[key]) as Appearance[K];
  return {
    ...DEFAULT_APPEARANCE,
    ...value,
    heading_font: pick('heading_font', HEADING_FONTS),
    text_intensity: pick('text_intensity', INTENSITIES),
    text_weight: pick('text_weight', WEIGHTS),
    text_color: pick('text_color', TEXT_COLOR_OPTIONS),
    page_background: pick('page_background', PAGE_BACKGROUNDS),
    print_background: pick('print_background', PRINT_BACKGROUNDS),
  } as Appearance;
}

function hexToRgb(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mix(a: string, b: string, weightOfB: number): string {
  const ca = hexToRgb(a);
  const cb = hexToRgb(b);
  const out = ca.map((v, i) => Math.round(v + ((cb[i] ?? v) - v) * weightOfB));
  return `#${out.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * Nuancier Mantine d'une couleur principale : la nuance 6 est la teinte du thème clair,
 * la nuance 4 celle du thème sombre (voir `primaryShade` dans le thème).
 */
export function accentPalette(key: AccentKey): MantineColorsTuple {
  const { light, dark } = ACCENTS[key];
  return [
    mix(light, '#ffffff', 0.9),
    mix(light, '#ffffff', 0.8),
    mix(light, '#ffffff', 0.62),
    mix(dark, '#ffffff', 0.25),
    dark,
    mix(light, dark, 0.5),
    light,
    mix(light, '#000000', 0.12),
    mix(light, '#000000', 0.24),
    mix(light, '#000000', 0.36),
  ];
}

export function isAppearance(value: unknown): value is Appearance {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    (v.theme === 'clair' || v.theme === 'sombre') &&
    typeof v.accent === 'string' &&
    v.accent in ACCENTS &&
    typeof v.font === 'string' &&
    SIZES.some(([s]) => s === v.size)
  );
}
